import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

import {
  useBusinessEstablishmentStore,
  useBusinessUnitStore,
  useCorporationStore,
  useJobListingStore,
  useOrganizationStore,
  usePersonalStore,
} from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import { replaceSegmentsFromRoutes } from "helpers/replaceSegmentsFromRoutes";
import { useGetMainPathType } from "hooks/useGetMainPathType";

import { TBreadcrumbItem, TRouteSegment } from "./types";
import { RouteType } from "types/index";
import { MAIN_PATH_MAP } from "./constants";
import {
  businessEstablishmentSlugParam,
  buSlugParam,
  corpSlugParam,
  jobListingSlugParam,
  organizationSlugParam,
  personalSlugParam,
} from "routes/constants";
import { routes } from "routes/routes";

const MAX_BREADCRUMB_ITEMS = 6;

const useBreadcrumb = () => {
  const user = usePersonalStore((state) => state.user);
  const corporation = useCorporationStore((state) => state.selectedCorp);
  const businessUnit = useBusinessUnitStore((state) => state.selectedBU);
  const selectedBusinessEstablishment = useBusinessEstablishmentStore(
    (state) => state.selectedBusinessEstablishment
  );
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization
  );
  const selectedJobListing = useJobListingStore(
    (state) => state.selectedJobListing
  );

  const { t } = useTranslationContext();
  const location = useLocation();
  const entityType = useGetMainPathType();

  const mainPath = useMemo(() => {
    return MAIN_PATH_MAP?.[entityType];
  }, [entityType]);

  const breadcrumbItems: TBreadcrumbItem[] = useMemo(() => {
    const items: TBreadcrumbItem[] = [
      {
        label: t(mainPath.label),
        link: mainPath?.link,
      },
    ];

    const currentPath = getMatchPath(routes, location.pathname);
    const splitedPaths = currentPath?.split("/")?.filter((path) => path) || [];
    const currentSplitedPath =
      location.pathname.split("/")?.filter((path) => path) || "";
    let routeSegment: TRouteSegment = {
      params: [],
      replacer: [],
    };

    // Get all queryparams key & value to be replaced
    for (let i = 0; i < splitedPaths.length; i++) {
      if (splitedPaths[i].startsWith(":")) {
        routeSegment = {
          params: [...routeSegment.params, splitedPaths[i].replaceAll(":", "")],
          replacer: [...routeSegment.replacer, currentSplitedPath[i]],
        };
      }
    }

    // Get all -1 routes from the given exiting route
    const linkedRoutes = splitedPaths
      .map((path, index) => {
        let combinedPath = `/${path}`;

        if (index > 0) {
          let tmpCombinedPath = "";
          for (let i = 0; i <= index; i++) {
            tmpCombinedPath += `/${splitedPaths[i]}`;
          }
          combinedPath = tmpCombinedPath;
        }

        const route = routes.find((route) => route.path === combinedPath);
        return route;
      })
      ?.filter((route) => !!route?.path);

    /**
     * Avoid show breadcrumbs if hideBreadcrumb = true
     * Map all the breadcrumbs that required dynamic value
     */
    const newItems: TBreadcrumbItem[] = linkedRoutes
      .filter((route) => !route?.hideBreadcrumb)
      ?.map((route) => {
        let label = route?.label ? t(route.label) : "";
        switch (route?.label) {
          case personalSlugParam:
            label = user.fullName || "";
            break;
          case corpSlugParam:
            label = corporation?.name || "";
            break;
          case buSlugParam:
            label = businessUnit?.name || "";
            break;
          case businessEstablishmentSlugParam:
            label = selectedBusinessEstablishment?.name || "";
            break;
          case organizationSlugParam:
            label = selectedOrganization?.name || "";
            break;
          case jobListingSlugParam:
            label = selectedJobListing?.title || "";
            break;
          default:
            break;
        }

        const constructedPath = replaceSegmentsFromRoutes({
          route: route?.path || "",
          ...routeSegment,
        });

        return {
          label: label,
          link: constructedPath,
        };
      });

    return [...items, ...newItems];
  }, [
    mainPath,
    location,
    user,
    corporation,
    businessUnit,
    selectedBusinessEstablishment,
    selectedOrganization,
    selectedJobListing,
    t,
  ]);

  // Split breadcrumbs if too many and join with ...
  const splitedBreadcrumbsItem = useMemo(() => {
    if (breadcrumbItems.length > MAX_BREADCRUMB_ITEMS) {
      const splitedBreadcrumbs = breadcrumbItems.slice(
        Math.max(breadcrumbItems.length - (MAX_BREADCRUMB_ITEMS - 1), 1)
      );
      const shadowedBreadcrumb =
        breadcrumbItems[breadcrumbItems.length - MAX_BREADCRUMB_ITEMS];

      return [
        breadcrumbItems[0],
        {
          ...shadowedBreadcrumb,
          label: "...",
        },
        ...splitedBreadcrumbs,
      ];
    }

    return breadcrumbItems;
  }, [breadcrumbItems]);

  return {
    breadcrumbItems: splitedBreadcrumbsItem,
  };
};

export default useBreadcrumb;

const getMatchPath = (routes: RouteType[], pathname: string): string => {
  let currentPath = "";

  for (const route of routes) {
    if (
      matchPath(
        {
          path: route.path,
          end: true,
        },
        pathname
      )
    ) {
      currentPath = route.path;
      break;
    }
  }

  return currentPath;
};
